import { AuctionStatus } from '@/types/LiveAuctionState';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { CurrencyCode, Item } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import { getBidHistory } from '@/redux/modules/bidHistory';
import { getCatalogStatus } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getIsExtended,
    getIsItemSummaryLoaded,
    getItemCurrentTimedPlusEndTime,
    getItemIndex,
    getItemModelBidActivityCount,
    getItemModelBidCount,
    getItemModelBuyNowPrice,
    getItemModelBuyNowStatus,
    getItemModelCatalogId,
    getItemModelHasReserve,
    getItemModelHighBidEstimate,
    getItemModelIsAvailable,
    getItemModelIsLocked,
    getItemModelIsPassed,
    getItemModelIsSold,
    getItemModelLotNumber,
    getItemModelLowBidEstimate,
    getItemModelSalePrice,
    getItemModelStartPrice,
    getItemModelStartTime,
    getItemReserveMet,
    getItemSummaryLeadingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getLiveCatalogCurrentItemIndex,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBid,
} from '@/redux/modules/console';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { getSavedItemCountNum } from '@/redux/modules/savedItemCount';
import { useAppSelector } from '@/redux/hooks';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';
import { useGetItemBidChipSuggestions } from './useGetItemBidChipSuggestions';
import { useGetShowCountdown } from './useGetShowCountdown';
import { useMemo } from 'react';
import { useNoReservePriceBadgeExperiment } from '@/utils/amplitude/experiments/hooks/useNoReservePriceBadgeExperiment';

type Props = {
    itemId: number;
    onCountdownEnd?: () => void;
};

export type BidCanvasItemData = Item & {
    currentPrice: number;
    isReserveMet: boolean;
    liveItemIndex: number;
    lotStatus?: Item['lotStatus'];
    nextBidAmount: number;
    numberOfBids: number;
    numberOfWatchers: number;
    saleStartTs: number;
    shouldShowTimer: boolean;
    showNoReserve: boolean;
};

export const useGetBidCanvasItemData = ({ itemId, onCountdownEnd }: Props): BidCanvasItemData => {
    // no reserve item experiment
    const hasNoReservePriceBadgeExperiment = useNoReservePriceBadgeExperiment();
    const hasReserve = useAppSelector((state) => getItemModelHasReserve(state, itemId));
    const isLoaded = useAppSelector((state) => getIsItemSummaryLoaded(state, itemId));
    const showNoReserve = isLoaded && hasNoReservePriceBadgeExperiment && !hasReserve;

    const bidActivityCount = useAppSelector((state) => getItemModelBidActivityCount(state, itemId));
    const bidCount = useAppSelector((state) => getItemModelBidCount(state, itemId));
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemModelHighBidEstimate(state, itemId));
    const index = useAppSelector((state) => getItemIndex(state, itemId));
    const isAvailable = useAppSelector((state) => getItemModelIsAvailable(state, itemId));
    const isLocked = useAppSelector((state) => getItemModelIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemModelIsPassed(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemReserveMet(state, itemId));
    const isSold = useAppSelector((state) => getItemModelIsSold(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId)) || 0;
    const lotNumber = useAppSelector((state) => getItemModelLotNumber(state, itemId));
    const lowBidEstimate = useAppSelector((state) => getItemModelLowBidEstimate(state, itemId));
    const soldPrice = useAppSelector((state) => getItemModelSalePrice(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemModelStartTime(state, itemId));
    const startingPrice = useAppSelector((state) => getItemModelStartPrice(state, itemId)) || 0;
    const bidHistory = useAppSelector((state) => getBidHistory(state, itemId));
    const isTimedPlusExtended = useAppSelector((state) => getIsExtended(state, itemId));
    const timedPlusSaleEndTime = useAppSelector((state) => getItemCurrentTimedPlusEndTime(state, itemId));

    const bidCountValue = Math.max(bidActivityCount, bidCount);

    const liveItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));

    const liveItemLeadingBid = useAppSelector((state) => getLiveItemDataLeadingBid(state, itemId));

    const numberOfWatchers = useAppSelector((state) => getSavedItemCountNum(state, itemId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isLiveItem = liveItemIndex === index && catalogStatus === 'live';

    const shouldShowTimer = useGetShowCountdown({ catalogId, itemId, onCountdownEnd });

    const buyNowStatus = useAppSelector((state) => getItemModelBuyNowStatus(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemModelBuyNowPrice(state, itemId));
    const buyItNowAvailable =
        buyNowStatus === BuyNowStatus.Available &&
        (catalogStatus === AuctionStatus.Done || catalogStatus === undefined);

    const currentPrice = useMemo(() => {
        const safeSoldOrStartingPrice = soldPrice > 0 ? soldPrice : startingPrice;

        if (buyItNowAvailable && buyNowPrice) {
            return buyNowPrice;
        }

        if (isLiveItem) {
            return liveItemLeadingBid || leadingBid || safeSoldOrStartingPrice;
        }

        return leadingBid || safeSoldOrStartingPrice;
    }, [buyItNowAvailable, buyNowPrice, isLiveItem, liveItemLeadingBid, leadingBid, soldPrice, startingPrice]);

    const lotStatus = useMemo((): Item['lotStatus'] => {
        if (isPassed) {
            return 'passed';
        }

        const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;

        if (isUnsold) {
            return 'pending';
        }

        if (isSold) {
            return 'sold';
        }

        if (isLiveItem) {
            return 'live';
        }

        if (isLocked || catalogStatus === 'paused') {
            return 'locked';
        }

        if (isAvailable) {
            return 'upcoming';
        }

        return undefined;
    }, [isPassed, isLocked, isSold, isLotOpen, isLiveItem, catalogStatus, isAvailable]);

    const memoizedBidHistory = useDeepCompareMemoize(bidHistory);
    const bidHistoryTyped: Item['bidHistory'] = useMemo(
        () =>
            memoizedBidHistory.map((historyItem) => {
                return {
                    ...historyItem,
                    currency: historyItem.currency as CurrencyCode,
                };
            }),
        [memoizedBidHistory]
    );

    const numberOfBids = Boolean(Number(bidCountValue)) ? bidCountValue : 0;
    const nextBidAmount = lotStatus === 'live' ? currentAskPrice : currentPrice;
    const saleStart = Boolean(saleStartTs) ? saleStartTs * 1000 : 0;
    const timedPlusSaleEndTimeTs = timedPlusSaleEndTime ? timedPlusSaleEndTime * 1000 : 0;
    const bidPanelChipIncrementOptions = useGetItemBidChipSuggestions({ hasReserve, itemId });

    return useMemo(
        () => ({
            bidHistory: bidHistoryTyped,
            bidPanelChipIncrementOptions,
            buyItNowAvailable,
            currentPrice,
            hasReserve,
            highBidEstimate,
            index,
            isReserveMet,
            isTimedPlusExtended,
            liveItemIndex,
            lotNumber,
            lotStatus,
            lowBidEstimate,
            nextBidAmount,
            numberOfBids,
            numberOfWatchers,
            saleEndTime: timedPlusSaleEndTimeTs,
            saleStartTs: saleStart,
            shouldShowTimer,
            showNoReserve,
            soldPrice,
            startingPrice,
        }),
        [
            bidHistoryTyped,
            bidPanelChipIncrementOptions,
            buyItNowAvailable,
            currentPrice,
            hasReserve,
            highBidEstimate,
            index,
            isReserveMet,
            isTimedPlusExtended,
            liveItemIndex,
            lotNumber,
            lotStatus,
            lowBidEstimate,
            nextBidAmount,
            numberOfBids,
            numberOfWatchers,
            timedPlusSaleEndTimeTs,
            saleStart,
            shouldShowTimer,
            showNoReserve,
            soldPrice,
            startingPrice,
        ]
    );
};
